import cx from 'classnames';
import { func, string } from 'prop-types';
import React, { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { usersRoles } from '../../constants';
import { messagesActions } from '../../store/actions';
import {
  getSavedMessages,
  getCurrentDialogId,
  getIsOutcomingLast,
  getIsMessageLoading,
  getMessagesItems,
  getIsMessageBlocked,
  getIsEmex,
  getSenderId,
  getTypingExperts,
  getMessageButtons,
  getIsCurrentDialogArchive,
  getActiveExpert,
} from '../../store/selectors';

import BoxMessage from '../BoxMessage';
import ImagesModal from '../ImagesModal';
import ExpertsCarousel from '../ExpertsCarousel';
import Loader from '../Loader';
import MessagesList from '../Messages';
import QuickAnswers from '../QuickAnswers';
import Scroll from '../Scroll';
import WidgetFooter from '../WidgetFooter';

import s from './index.module.css';

const MAX_MOBILE_VIEWPORT_PX = 480;

const ChatWindow = ({
  imagePreview,
  sendMessage,
  addBuyerChoice,
  sendMessageWithFile,
  onPostFile,
  resetImagePreview,
  onType,
  isWidgetPath,
}) => {
  const dispatch = useDispatch();
  const isEmex = useSelector(getIsEmex);
  const isOutcomingLast = useSelector(getIsOutcomingLast);
  const isLoading = useSelector(getIsMessageLoading);
  const savedMessages = useSelector(getSavedMessages);
  const currentDialogId = useSelector(getCurrentDialogId);
  const currentExpert = useSelector(getActiveExpert);
  const messages = useSelector(getMessagesItems) || [];
  const senderId = useSelector(getSenderId);
  const isMessageBlocked = useSelector(getIsMessageBlocked);
  const isArchiveDialog = useSelector(getIsCurrentDialogArchive);
  const typingExperts = useSelector(getTypingExperts);
  const quickAnswers = useSelector(getMessageButtons) || [];

  const [isInitialRender, setInitialRender] = useState(true);
  const [actualMessages, setActualMessages] = useState(undefined);
  const [actualTypingExperts, setActualTypingExperts] = useState(undefined);
  const chatWindowEl = useRef();
  const isMobile = window.innerWidth <= MAX_MOBILE_VIEWPORT_PX;
  const isInputBlocked = isMessageBlocked || isArchiveDialog;

  const handleSend = (text) => {
    sendMessage(text, senderId, currentDialogId);
  };

  const handleSendWithFile = ({image, caption}) => {
    sendMessageWithFile(caption, image, currentDialogId);
  };

  const handleBuyerChoice = (item) => {
    addBuyerChoice(item, senderId, currentDialogId);
  };

  const handleSelectLocation = (location) => {
    dispatch(messagesActions.addLocationChoice(currentDialogId, location));
  };

  const handleBoxMessageFocus = () => {
    const wrapperEl = chatWindowEl.current && chatWindowEl.current.contentWrapperEl;
    if (isMobile && wrapperEl) {
      // disablePageScroll();

      setTimeout(() => {
        wrapperEl.scrollTo({
          top: chatWindowEl.current && chatWindowEl.current.contentWrapperEl.scrollHeight,
          behavior: 'smooth',
        });
      }, 300);
    }
  };

  useEffect(() => {
    const isScrollDisabled = chatWindowEl.current && chatWindowEl.current.contentWrapperEl;

    if (isScrollDisabled) {
      // disablePageScroll(chatWindowEl.current.contentWrapperEl);
    }
  }, [chatWindowEl]);

  useEffect(() => {
    setTimeout(() => {
      const wrapperEl = chatWindowEl.current && chatWindowEl.current.contentWrapperEl;
      if (wrapperEl && (isOutcomingLast || isInitialRender || typingExperts)) {
        wrapperEl.scrollTop = wrapperEl.scrollHeight;
        if (!isInitialRender) {
          setInitialRender(false);
        }
      }
    });
  }, [isOutcomingLast, messages, setInitialRender, isInitialRender, typingExperts]);

  const sendMessageToHost = () => {
    const { parent } = window.frames;
    if (parent) {
      parent.postMessage('unread_message', '*');
    }
  };

  useEffect(() => {
    const hasExpertMessages = messages.some(
      (message) => message && message.sender && message.sender.type_id === usersRoles.EXPERT,
    );
    if (hasExpertMessages && !isOutcomingLast && isWidgetPath) {
      sendMessageToHost(isOutcomingLast);
    }
  }, [isOutcomingLast, messages, isWidgetPath]);

  const hasQuickAnswers = quickAnswers && quickAnswers.length > 0;

  const scrollToEndOfMessageBox = (scrollType = 'smooth', timeout = 400) => {
    setTimeout(() => {
      const wrapperEl = chatWindowEl.current && chatWindowEl.current.contentWrapperEl;
      if (wrapperEl) {
        requestAnimationFrame(() => {
          wrapperEl.scrollTo({
            top: wrapperEl.scrollHeight,
            behavior: scrollType,
          });
        });
      }
    }, timeout);
  }

  useEffect(() => {
    scrollToEndOfMessageBox();
  }, [quickAnswers]);

  useEffect(() => {
    const savedMessagesById = savedMessages.filter(i => i.dialogId === currentDialogId);
    if (savedMessagesById[0] && savedMessagesById[0].messages.length > 0) {
      setActualMessages(savedMessagesById[0].messages);
      scrollToEndOfMessageBox('instant', 300);
    }
  }, [currentDialogId]);

  useEffect(() => {
    const filteredFetchedMessages = messages.filter(i => i.dialog_id === currentDialogId) || [];
    setActualMessages(filteredFetchedMessages);
  }, [currentDialogId, messages]);

  useEffect(() => {
    const filteredTypingExperts = typingExperts?.filter(i => i.expertId === currentExpert?.expertId) || [];
    setActualTypingExperts(filteredTypingExperts);
  }, [currentDialogId, typingExperts]);

  return (
    <div className={cx(s.chatWindow, { [s.chatWindowEmex]: isEmex })}>
      <div className={s.chatWrapper}>
        <div
          className={cx({
            [s.loaderWrapper]: isLoading,
            [s.hiddenLoader]: !isLoading,
          })}
        >
          <Loader text="Загрузка" />
          <span className={s.loaderText}>Загрузка</span>
        </div>
        <Scroll
          className={cx({
            [s.messageListWrapper]: !isLoading,
            [s.hiddenMessages]: isLoading,
          })}
          ref={chatWindowEl}
        >
          <div className={s.chatAria} id="chat-scroll">
            <ExpertsCarousel />
            <MessagesList
              chatWindowRef={chatWindowEl}
              onSelectLocation={handleSelectLocation}
              isWidgetPath={isWidgetPath}
              actualMessages={actualMessages}
              typingExperts={actualTypingExperts}
            />
          </div>
        </Scroll>
      </div>
      {hasQuickAnswers && <QuickAnswers items={quickAnswers} addBuyerChoice={handleBuyerChoice} />}
      {isWidgetPath && <WidgetFooter />}
      <div className={cx(s.footer, { [s.hiddenFooter]: isInputBlocked })}>
        <BoxMessage
          onPostFile={onPostFile}
          sendMessage={handleSend}
          imageSrc={imagePreview}
          sendMessageWithFile={handleSendWithFile}
          resetImagePreview={resetImagePreview}
          onType={onType}
          onFocus={handleBoxMessageFocus}
          isWidgetPath={isWidgetPath}
        />
      </div>
      <ImagesModal />
    </div>
  );
};

ChatWindow.propTypes = {
  imagePreview: string,
  sendMessageWithFile: func,
  addBuyerChoice: func,
  sendMessage: func,
  onPostFile: func,
  resetImagePreview: func,
};

export default ChatWindow;
